<script setup>
import { computed, inject, ref } from 'vue';
import { X, AlertCircle } from '@lucide/vue';
import Button from '../../components/ui/Button.vue';
import BrandIcon from '../../components/BrandIcon.vue';
import MessageList from '../../components/chat/MessageList.vue';
import WelcomeState from '../../components/chat/WelcomeState.vue';
import Composer from '../../components/chat/Composer.vue';
import { useTranslations } from '../../composables/useTranslations.js';
import { useVisualViewport } from '../../composables/useVisualViewport.js';

const props = defineProps({
    open: { type: Boolean, default: false },
    messages: { type: Array, default: () => [] },
    status: { type: String, default: 'idle' },
    isBusy: { type: Boolean, default: false },
    canRetry: { type: Boolean, default: false },
    isBlocked: { type: Boolean, default: false },
});

defineEmits(['close', 'send', 'send-prompt', 'retry']);

const config = inject('aia.config');
const { t } = useTranslations(config?.locale);

const isEmpty = computed(() => props.messages.length === 0);

// While open on mobile, track the visual viewport so the drawer (and its
// bottom-anchored composer) stays above the on-screen keyboard. The browser
// toolbar/navbar case is handled by the `h-[100dvh]` class below.
const rootRef = ref(null);
useVisualViewport(rootRef, computed(() => props.open));

const statusBanner = computed(() => {
    if (props.status === 'blocked') return t('states.paused');
    if (props.status === 'rate-limited') return t('states.rateLimited');
    if (props.status === 'offline') return t('states.offline');
    if (props.status === 'error') return t('states.error');
    return null;
});
</script>

<template>
    <aside
        ref="rootRef"
        class="aia-d2-drawer fixed top-0 right-0 h-[100dvh] flex flex-col border-l border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] shadow-[-24px_0_60px_-24px_rgba(0,0,0,0.6)] transition-transform duration-[420ms] overflow-hidden"
        :class="open ? 'translate-x-0' : 'translate-x-full'"
        :style="{
            width: 'min(420px, 100%)',
            zIndex: 'calc(var(--aia-z) - 1)',
        }"
        role="dialog"
        aria-modal="false"
        :aria-label="t('title')"
        :aria-hidden="!open"
    >
        <!-- Ambient aurora behind the chat — three soft blurred radial spots
             (violet, magenta, cyan) that slowly drift. Matches the landing's
             hero aurora-1/2/3 visual language but contained to the drawer.
             Hidden when a variant supplies a photo background. -->
        <div class="aia-d2-aurora aia-d2-aurora--violet" aria-hidden="true" />
        <div class="aia-d2-aurora aia-d2-aurora--magenta" aria-hidden="true" />
        <div class="aia-d2-aurora aia-d2-aurora--cyan" aria-hidden="true" />

        <!-- Readability scrim over the photo background (transparent unless a
             variant sets --aia-drawer-scrim). -->
        <div class="aia-d2-scrim" aria-hidden="true" />

        <!-- Thin animated gradient stripe at the very top edge of the drawer.
             A "live signal" hair-line that telegraphs "this surface is AI". -->
        <div class="aia-d2-top-stripe aia-gradient-bg-accent" aria-hidden="true" />

        <!-- Header -->
        <header class="relative flex items-center justify-between gap-3 border-b border-[var(--border)] px-5 py-4 shrink-0 backdrop-blur-sm">
            <div class="flex items-center gap-2.5">
                <BrandIcon :size="35" :stroke-width="2.25" />
                <div>
                    <h2 class="text-sm font-semibold leading-tight tracking-tight">{{ t('title') }}</h2>
                    <p class="text-[11px] text-[var(--muted-foreground)] leading-tight flex items-center gap-1.5">
                        <span class="aia-d2-status-dot" aria-hidden="true" />
                        MoonGroup
                    </p>
                </div>
            </div>
            <Button
                variant="ghost"
                size="icon"
                class="!h-8 !w-8"
                :aria-label="t('actions.close')"
                @click="$emit('close')"
            >
                <X :size="16" />
            </Button>
        </header>

        <!-- Body (relative so it stacks above the aurora) -->
        <div v-if="isEmpty" class="relative flex-1 overflow-y-auto overscroll-contain px-5">
            <WelcomeState @select="(prompt) => $emit('send-prompt', prompt)" />
        </div>
        <MessageList
            v-else
            class="relative"
            :messages="messages"
            :streaming="isBusy"
            :status="status"
        />

        <!-- Status banner -->
        <div
            v-if="statusBanner"
            class="relative mx-4 mb-2 flex items-center justify-between gap-3 rounded-md border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-xs text-[var(--muted-foreground)]"
            role="alert"
        >
            <span class="inline-flex items-center gap-2">
                <AlertCircle :size="14" />
                {{ statusBanner }}
            </span>
            <button
                v-if="canRetry"
                type="button"
                class="text-[var(--primary)] hover:underline"
                @click="$emit('retry')"
            >
                {{ t('actions.retry') }}
            </button>
        </div>

        <!-- Composer (lives inside the drawer in Design 02) -->
        <Composer
            class="relative shrink-0"
            :disabled="isBusy || isBlocked"
            @send="(text) => $emit('send', text)"
        />
    </aside>
</template>

<style scoped>
.aia-d2-drawer {
    transition-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
    will-change: transform;
    /* Default: subtle radial vignette for depth. A variant can swap in a photo
     * via --aia-drawer-bg-image (+ size/position). */
    background-image: var(
        --aia-drawer-bg-image,
        radial-gradient(120% 60% at 50% 0%, rgba(139, 92, 246, 0.08), transparent 70%),
        radial-gradient(80% 50% at 100% 100%, rgba(34, 211, 238, 0.05), transparent 70%)
    );
    background-size:     var(--aia-drawer-bg-size, auto);
    background-position: var(--aia-drawer-bg-position, 0 0);
    background-repeat:   no-repeat;
}

/* Readability scrim over a photo background — transparent unless a variant
 * sets --aia-drawer-scrim. Sits above the bg image, below the content. */
.aia-d2-scrim {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: var(--aia-drawer-scrim, transparent);
}

/* ---- Aurora layer (3 soft blurred orbs) ----
 * Hidden when a variant supplies a photo background
 * (moongroup-space sets --aia-drawer-decor-display: none). */
.aia-d2-aurora {
    display: var(--aia-drawer-decor-display, block);
    position: absolute;
    pointer-events: none;
    border-radius: 9999px;
    filter: blur(60px);
    opacity: 0.5;
    z-index: 0;
    will-change: transform, opacity;
}
.aia-d2-aurora--violet {
    width: 320px; height: 320px;
    top: -80px; right: -100px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.55), transparent 70%);
    animation: aia-d2-drift-1 14s ease-in-out infinite;
}
.aia-d2-aurora--magenta {
    width: 260px; height: 260px;
    top: 40%; left: -120px;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.35), transparent 70%);
    animation: aia-d2-drift-2 18s ease-in-out infinite;
    opacity: 0.35;
}
.aia-d2-aurora--cyan {
    width: 280px; height: 280px;
    bottom: -60px; right: -80px;
    background: radial-gradient(circle, rgba(34, 211, 238, 0.4), transparent 70%);
    animation: aia-d2-drift-3 16s ease-in-out infinite;
    opacity: 0.32;
}

@keyframes aia-d2-drift-1 {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(-20px, 30px, 0); }
}
@keyframes aia-d2-drift-2 {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(30px, -20px, 0); }
}
@keyframes aia-d2-drift-3 {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(-25px, -25px, 0); }
}

/* ---- Thin animated gradient stripe at the top edge ---- */
.aia-d2-top-stripe {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    z-index: 2;
    opacity: 0.85;
}

/* ---- Header "live" status dot (matches the AI accent palette) ---- */
.aia-d2-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: #22d3ee;
    box-shadow: 0 0 6px rgba(34, 211, 238, 0.8);
    animation: aia-d2-status-pulse 2.6s ease-in-out infinite;
}
@keyframes aia-d2-status-pulse {
    0%, 100% { opacity: 1;    transform: scale(1);    }
    50%      { opacity: 0.45; transform: scale(0.85); }
}

/* ---- Brand-mark avatar — gentle breathing scale ---- */
.aia-d2-avatar {
    animation: aia-d2-avatar-breathe 4s ease-in-out infinite;
}
@keyframes aia-d2-avatar-breathe {
    0%, 100% { transform: scale(1);    box-shadow: 0 4px 12px -4px rgba(139, 92, 246, 0.55); }
    50%      { transform: scale(1.04); box-shadow: 0 6px 20px -4px rgba(139, 92, 246, 0.75); }
}

@media (prefers-reduced-motion: reduce) {
    .aia-d2-drawer { transition: opacity 140ms; }
    .aia-d2-aurora,
    .aia-d2-status-dot,
    .aia-d2-avatar { animation: none; }
}
</style>
